/**
 * The driver screen: personal safety gear and its expirations.
 *
 * Gear follows the driver, not the car (§2) — an item with `ownerType: 'driver'`
 * — which is why this is its own screen rather than a section of a car's. The
 * expirations shown here are the same `certs` the timeline draws, filtered to
 * the ones with no car behind them.
 */

import { useState } from 'react';
import { Bell, CalendarDays, CircleDashed, Flag, Pencil, Plus, ShieldCheck, User } from 'lucide-react';

import { EXPIRY_NOTICE_DAYS } from '@svcdue/core';
import type { CertOccurrence, DriverDto, ItemDto, TimelineResponse } from '@svcdue/contracts';

import { useDrivers, useItems, useMarkChecked, useTimeline } from '../api/hooks';
import { Empty, ErrorState, Loading } from '../components/states';
import { Eyebrow, Pill } from '../components/primitives';
import { InspectCard } from '../components/InspectCard';
import { useGarageContext, usePermissions } from '../garage/GarageProvider';
import { fmtLong, today } from '../lib/format';

export function DriverScreen({
  openAddDriver,
  openEditDriver,
  openAddGear,
  openEditGear,
  openAddSafety,
}: {
  openAddDriver: () => void;
  openEditDriver: (driver: DriverDto) => void;
  openAddGear: (driver: DriverDto) => void;
  openEditGear: (driver: DriverDto, item: ItemDto) => void;
  openAddSafety: (driver: DriverDto) => void;
}) {
  const { garageId } = useGarageContext();
  const { canManageFleet } = usePermissions();
  const drivers = useDrivers(garageId);
  const items = useItems(garageId);
  const timeline = useTimeline(garageId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (drivers.isPending || items.isPending || timeline.isPending) return <Loading label="Loading driver…" />;
  if (drivers.isError) return <ErrorState error={drivers.error} onRetry={() => void drivers.refetch()} />;
  if (timeline.isError) return <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />;
  if (!drivers.data || !items.data || !timeline.data) return <Loading />;

  if (drivers.data.length === 0) {
    return (
      <div className="screen">
        <Eyebrow>Driver</Eyebrow>
        <Empty
          icon={<User size={20} />}
          title="No drivers yet"
          hint={
            canManageFleet
              ? 'Add a driver to track licences, suits, helmets and restraints against them.'
              : 'The owner has not added a driver yet.'
          }
          action={
            canManageFleet ? (
              <button className="act" onClick={openAddDriver}>
                <Plus size={12} /> Add driver
              </button>
            ) : undefined
          }
        />
      </div>
    );
  }

  const driver = drivers.data.find((d) => d.id === selectedId) ?? drivers.data[0]!;

  return (
    <div className="screen">
      <Eyebrow>Driver</Eyebrow>

      {/* The switch strip carries the owner's "add" affordance too, the same as
          the garage's car switch — so it renders whenever there is more than one
          driver to pick between or an owner who can add another. */}
      {(drivers.data.length > 1 || canManageFleet) && (
        <div className="car-switch">
          {drivers.data.length > 1 &&
            drivers.data.map((d) => (
              <button
                key={d.id}
                className={'cs-btn' + (d.id === driver.id ? ' cs-on' : '')}
                onClick={() => setSelectedId(d.id)}
              >
                {d.name}
              </button>
            ))}
          {canManageFleet && (
            <button className="cs-add" onClick={openAddDriver} aria-label="Add driver">
              <Plus size={15} />
            </button>
          )}
        </div>
      )}

      <DriverDetail
        driver={driver}
        items={items.data}
        timeline={timeline.data}
        garageId={garageId!}
        onEdit={canManageFleet ? () => openEditDriver(driver) : undefined}
        onAddGear={canManageFleet ? () => openAddGear(driver) : undefined}
        onEditGear={canManageFleet ? (item) => openEditGear(driver, item) : undefined}
        onAddSafety={canManageFleet ? () => openAddSafety(driver) : undefined}
      />
    </div>
  );
}

function DriverDetail({
  driver,
  items,
  timeline,
  garageId,
  onEdit,
  onAddGear,
  onEditGear,
  onAddSafety,
}: {
  driver: DriverDto;
  items: ItemDto[];
  timeline: TimelineResponse;
  garageId: string;
  /** Owner-only: absent for crew, who read gear but never restructure the fleet. */
  onEdit?: () => void;
  /** Owner-only, like every gear write — gear is an owner-managed item (routes/items.ts). */
  onAddGear?: () => void;
  onEditGear?: (item: ItemDto) => void;
  onAddSafety?: () => void;
}) {
  const { canLogMaintenance } = usePermissions();
  const markChecked = useMarkChecked(garageId);

  const gear = items.filter((i) => i.ownerType === 'driver' && i.ownerId === driver.id);
  const gearIds = new Set(gear.map((g) => g.id));

  // Driver certs are the ones with no car behind them — the same filter the
  // public view uses to keep a person's licence private (routes/public.ts).
  const certs = timeline.certs.filter((c) => c.carId === null && gearIds.has(c.itemId));
  const inspections = timeline.inspections.filter((i) => i.carId === null && gearIds.has(i.itemId));

  // The soonest expiration is the one that stops you racing, so it leads.
  const headline = certs[0];

  return (
    <>
      <h1 className="h1">{driver.name}</h1>
      {onEdit && (
        <button className="link car-edit" onClick={onEdit}>
          <Pencil size={12} /> Edit driver
        </button>
      )}
      <p className="lede">
        Personal gear follows the driver across every car. Expirations are flagged at{' '}
        <b className="c-ink">{EXPIRY_NOTICE_DAYS[0]}</b> and <b className="c-ink">{EXPIRY_NOTICE_DAYS[1]}</b> days, and
        emailed to the garage.
      </p>

      {headline && <LicenceCard cert={headline} compNumber={driver.compNumber} />}

      {/* Owner-only: gear is an owner-managed item (routes/items.ts), so crew see
          it but never the affordance to add one — a button that 403s is a broken
          promise, the same rule the garage's "Add part" follows. */}
      {onAddGear && (
        <div className="add-btns">
          <button className="add-part-btn" onClick={onAddGear}>
            <Plus size={15} /> Add gear to {driver.name}
          </button>
          {/* The NHRA safety starter: helmet, suit, restraint and the rest this
              driver needs at the car's ET. A convenience, not the rulebook. */}
          {onAddSafety && (
            <button className="add-part-btn add-safety-btn" onClick={onAddSafety}>
              <ShieldCheck size={15} /> Add usual safety equipment
            </button>
          )}
        </div>
      )}

      <div className="row-h">
        <h2 className="h2">Safety gear</h2>
        <span className="row-sub">SFI &amp; Snell</span>
      </div>

      {gear.length === 0 ? (
        <Empty
          icon={<ShieldCheck size={20} />}
          title="No gear tracked yet"
          hint={
            onAddGear
              ? 'Add the suit, helmet and restraint as safety items on this driver.'
              : 'The owner has not added any gear to this driver yet.'
          }
          action={
            onAddGear ? (
              <button className="act" onClick={onAddGear}>
                <Plus size={12} /> Add gear
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="gear-grid">
          {gear.map((g) => {
            const cert = certs.find((c) => c.itemId === g.id);
            return (
              <div className="gear" key={g.id}>
                <div className="gear-h">
                  <span className="gear-ic">
                    <ShieldCheck size={15} />
                  </span>
                  <div className="gear-h-right">
                    {cert && <Pill s={cert.status} />}
                    {/* Owner-only edit, opening the same AddPart sheet in edit
                        mode — where delete also lives, behind its confirming tap.
                        So the card needs no delete of its own. */}
                    {onEditGear && (
                      <button className="part-edit" onClick={() => onEditGear(g)} aria-label={`Edit ${g.name}`}>
                        <Pencil size={13} />
                      </button>
                    )}
                  </div>
                </div>
                <b className="gear-name">{g.name}</b>
                <div className="gear-spec">{g.spec ?? g.partNumber ?? '—'}</div>
                <div className="gear-detail">{g.partNumber ?? ''}</div>
                {cert && (
                  <div className="gear-proj">
                    <CalendarDays size={11} /> {cert.status === 'over' ? 'Expired' : 'Expires'} {fmtLong(cert.date)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {inspections.length > 0 && (
        <div className="group">
          <div className="group-h group-h-muted">
            <CircleDashed size={14} /> Inspect by hand
          </div>
          <div className="inspect-grid">
            {inspections.map((inspection) => (
              <InspectCard
                key={inspection.itemId}
                inspection={inspection}
                canLog={canLogMaintenance}
                isPending={markChecked.isPending}
                onMarkChecked={() => markChecked.mutate({ itemId: inspection.itemId, checkedAt: today() })}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function LicenceCard({ cert, compNumber }: { cert: CertOccurrence; compNumber: string | null }) {
  return (
    <div className="card lic">
      <div className="lic-top">
        <div className="lic-badge">
          <Flag size={20} />
        </div>
        <div className="lic-id">
          <div className="lic-org">{compNumber ? `Comp #${compNumber}` : 'Next expiration'}</div>
          <b>{cert.itemName}</b>
        </div>
        <Pill s={cert.status} />
      </div>
      <div className="lic-exp">
        <div>
          <span>{cert.status === 'over' ? 'Expired' : 'Expires'}</span>
          <b>{fmtLong(cert.date)}</b>
        </div>
        <div className="lic-count">
          <b>{Math.abs(cert.daysUntil)}</b> days {cert.status === 'over' ? 'ago' : 'left'}
        </div>
      </div>

      {/**
       * The reminder ladder, stated as policy rather than as status.
       *
       * The mock showed "60-day notice · Sent Jul 7" against a green tick, and
       * that is **not portable**: whether a reminder actually fired lives in the
       * `notifications` table (§2, §6), and no route exposes it — there is no
       * `GET /notifications` in the API. Rendering "Sent" from a date
       * calculation would be the UI asserting something only the weekly job
       * knows, and asserting it wrongly the moment SES bounced. So this says
       * what *will* happen, which is true, instead of what did, which we cannot
       * see from here.
       */}
      <div className="lic-emails">
        {EXPIRY_NOTICE_DAYS.map((days) => (
          <div className={'email-chip ' + (cert.daysUntil <= days ? 'em-sent' : 'em-wait')} key={days}>
            <Bell size={12} />
            <div>
              <b>{days}-day notice</b>
              <span>{cert.daysUntil <= days ? 'Window reached' : `In ${cert.daysUntil - days} days`}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
